import { BookingType } from "../../../Booking-Form/typings/booking-inputs";
import {
  CONFERENCE_ZONE_TYPE_ID,
  ELECTRIC_CHARGING_PLACE_TYPE_ID,
  PARKING_PLACE_TYPE_ID,
  VEHICLE_PLACE_TYPE_ID,
  WORKPLACE_PLACE_TYPE_ID
} from "../../../Booking-Form/typings/place-and-zone-type-ids.constant";
import type { MetaData } from "../../../Login/typings/login.types";

/**
 * Flip to `true` when /meta persists and returns per-type `*BookingEnabled` flags.
 * While `false`, only `workplaceBookingEnabled` is shown in admin and used for gating.
 */
export const PER_TYPE_BOOKING_ENABLED_META_ACTIVE = false;

export type BookingTypeEnableMetaKey = keyof Pick<
  MetaData,
  | "workplaceBookingEnabled"
  | "parkingplaceBookingEnabled"
  | "electricchargingplaceBookingEnabled"
  | "vehicleBookingEnabled"
  | "conferencezoneBookingEnabled"
  | "visitorBookingEnabled"
>;

const DEFERRED_BOOKING_TYPE_META_KEYS = new Set<BookingTypeEnableMetaKey>([
  "parkingplaceBookingEnabled",
  "electricchargingplaceBookingEnabled",
  "vehicleBookingEnabled",
  "conferencezoneBookingEnabled",
  "visitorBookingEnabled"
]);

export type BookingTypeEnablingRowConfig = {
  id: string;
  metaKey: BookingTypeEnableMetaKey;
  labelKey: string;
  bookingType?: BookingType;
  isVisible: (ctx: BookingTypeEnablingVisibilityContext) => boolean;
};

export type BookingTypeEnablingVisibilityContext = {
  placeTypeIds: Set<number>;
  zoneTypeIds: Set<number>;
  visitorManagementEnabled: boolean;
};

export const BOOKING_TYPE_ENABLING_ROWS: readonly BookingTypeEnablingRowConfig[] = [
  {
    id: "workplace",
    metaKey: "workplaceBookingEnabled",
    labelKey: "enableWorkplaceBooking",
    bookingType: BookingType.WORKPLACE,
    isVisible: ({ placeTypeIds }) => placeTypeIds.has(WORKPLACE_PLACE_TYPE_ID)
  },
  {
    id: "parkingplace",
    metaKey: "parkingplaceBookingEnabled",
    labelKey: "enableParkingplaceBooking",
    bookingType: BookingType.PARKINGPLACE,
    isVisible: ({ placeTypeIds }) => placeTypeIds.has(PARKING_PLACE_TYPE_ID)
  },
  {
    id: "electricchargingplace",
    metaKey: "electricchargingplaceBookingEnabled",
    labelKey: "enableElectricChargingBooking",
    bookingType: BookingType.ELECTRICCHARGINGSTATIONPLACE,
    isVisible: ({ placeTypeIds }) => placeTypeIds.has(ELECTRIC_CHARGING_PLACE_TYPE_ID)
  },
  {
    id: "vehicle",
    metaKey: "vehicleBookingEnabled",
    labelKey: "enableVehicleBooking",
    bookingType: BookingType.VEHICLE,
    isVisible: ({ placeTypeIds }) => placeTypeIds.has(VEHICLE_PLACE_TYPE_ID)
  },
  {
    id: "conferencezone",
    metaKey: "conferencezoneBookingEnabled",
    labelKey: "enableConferenceZoneBooking",
    bookingType: BookingType.CONFERENCEZONE,
    isVisible: ({ zoneTypeIds }) => zoneTypeIds.has(CONFERENCE_ZONE_TYPE_ID)
  },
  {
    id: "visitor",
    metaKey: "visitorBookingEnabled",
    labelKey: "enableVisitorBooking",
    bookingType: BookingType.VISITOR,
    isVisible: ({ visitorManagementEnabled }) => visitorManagementEnabled
  }
] as const;

export function isPerTypeBookingEnabledMetaActive(metaKey: BookingTypeEnableMetaKey): boolean {
  if (metaKey === "workplaceBookingEnabled") {
    return true;
  }
  return PER_TYPE_BOOKING_ENABLED_META_ACTIVE;
}

export function getBookingTypeEnabled(meta: MetaData, metaKey: BookingTypeEnableMetaKey): boolean {
  if (!isPerTypeBookingEnabledMetaActive(metaKey)) {
    return true;
  }
  const value = meta[metaKey];
  return value ?? true;
}

export function filterVisibleBookingTypeEnablingRows(
  ctx: BookingTypeEnablingVisibilityContext
): BookingTypeEnablingRowConfig[] {
  const visible = BOOKING_TYPE_ENABLING_ROWS.filter(row => row.isVisible(ctx));
  if (PER_TYPE_BOOKING_ENABLED_META_ACTIVE) {
    return visible;
  }
  return visible.filter(row => !DEFERRED_BOOKING_TYPE_META_KEYS.has(row.metaKey));
}
